/**
 * React and utilities
 */
import React, { useCallback } from 'react';

/**
 * Using Styled Components for CSS
 */
import styled from 'styled-components';
import { maxWidthForIsMobile } from '../../styles/GlobalStyles';
import ButtonStyled from '../Form/Button';
import StyledCurrencyField from '../Form/CurrencyField';
import StyledCustomSelect from '../Form/CustomSelect';
import { theme } from '../../styles/GlobalTheme';

/**
 * The header navigation bar.
 */
const ExistingOffer = ({
  className,
  setStep,
  setData,
  data: { loanAmount, loanProgram, rate, totalFees, pointFees },
}) => {
  const handleCurrencyFieldClick = (event) => {
    event.target.setSelectionRange(
      event.target.value.length,
      event.target.value.length
    );
  };

  const handleNextStep = useCallback(() => {
    setData({ loanAmount });
    setStep('home');
  }, [setData, setStep, loanAmount]);

  return (
    <div className={className}>
      <h2>Tell us about your existing offer.</h2>

      <span>What is your loan amount?</span>

      <StyledCurrencyField
        setValue={(value) => setData({ loanAmount: value })}
        value={loanAmount}
        adornment={'$'}
        onChange={handleCurrencyFieldClick}
      />

      <span>What is the interest rate of your offer?</span>

      <StyledCurrencyField
        setValue={(value) => setData({ rate: value })}
        value={rate}
        adornment={'%'}
        onChange={handleCurrencyFieldClick}
      />

      <span>What is the loan period?</span>

      <StyledCustomSelect
        value={loanProgram}
        onChange={({ target }) => setData({ loanProgram: target.value })}
        options={[
          {
            value: 'Fixed30Year',
            label: '30 Year Fixed',
          },
          {
            value: 'Fixed15Year',
            label: '15 Year Fixed',
          },
          {
            value: 'ARM5Year',
            label: '5-1 ARM',
          },
          {
            value: 'Other',
            label: 'Other',
          },
        ]}
      />

      <span>What are the points and credits fees you will be paying?</span>

      <StyledCurrencyField
        setValue={(value) => setData({ pointFees: value })}
        value={pointFees}
        adornment={'$'}
        onChange={handleCurrencyFieldClick}
      />

      <span>What are the lender/origination fees you'd have to pay?</span>

      <StyledCurrencyField
        setValue={(value) => setData({ totalFees: value })}
        value={totalFees}
        adornment={'$'}
        onChange={handleCurrencyFieldClick}
      />

      <div id="action-container">
        <ButtonStyled id="back-button" onClick={() => setStep('scenario')}>
          Back
        </ButtonStyled>

        <ButtonStyled
          id="call-to-action-button"
          variant="contained"
          onClick={handleNextStep}
          disabled={
            !loanAmount ||
            !loanProgram ||
            !rate ||
            typeof totalFees !== 'number' ||
            typeof pointFees !== 'number'
          }
          style={{ background: theme.green8, borderColor: theme.green8 }}
        >
          Save & Next
        </ButtonStyled>
      </div>
    </div>
  );
};

/**
 * The Final Exported Container
 */
const StyledExistingOffer = styled(ExistingOffer)`
  ${StyledCurrencyField},
  ${StyledCustomSelect} {
    max-width: 300px;

    &&& .MuiInputLabel-root,
    input {
      color: black;
    }
  }

  ${StyledCurrencyField},
  ${StyledCustomSelect} {
    margin-bottom: 15px;
  }

  & > h2 {
    font-size: 2.5em;
    font-weight: 400;
    font-family: Majorant-Medium;

    margin-bottom: 32px;
  }

  & > span {
    font-weight: 400;
    font-family: Majorant-Medium;
    display: block;

    margin-top: 10px;
    margin-bottom: 16px;
  }

  #action-container {
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-top: 10vh;

    button {
      height: 43px;
    }

    #back-button {
      width: 159px;
    }

    #call-to-action-button {
      width: 177px;
    }

    @media screen and (max-width: ${maxWidthForIsMobile}) {
      flex-direction: column;
      gap: 10px;

      #back-button,
      #call-to-action-button {
        width: 100%;
      }

      ${StyledCustomSelect},
      ${StyledCurrencyField} {
        max-width: 100%;
      }
    }
  }
`;

StyledExistingOffer.displayName = 'ExistingOffer';

export default StyledExistingOffer;
